import { createRef, memo, useContext, useState } from "react";
import { Form } from "react-bootstrap";
import styled from "styled-components";

import AuthContext from "common/store/AuthContext";
import StatusContext from "common/store/StatusContext";
import { LastUpdatedInfo } from "common/types/LastUpdatedInfo";
import { REPORT_TYPE, TAB_ID } from "common/types/consts/Defines";

import { ReportDateDisplay } from "../molecules/ReportDataDisplay";
import LastUpdatedCtrl from "../molecules/LastUpdatedCtrl";

import { ILCalendarDlg } from "./ILCalendarDlg";
import { addDays } from "utils/DateTools";
import { genEditKey } from "utils/commonTools";
import { EditControl } from "./EditControl";
import { WeatherCtrl } from "../../weather/WeatherCtrl";
import { SaveDataHandler } from "common/weather/types/SaveDataHandler";

type Props = {
  title: string;
  subtitle?: string;
  reportType: REPORT_TYPE;
  tabId: TAB_ID;
  lastUpdated?: LastUpdatedInfo;
  showLastupdated?: boolean;
  onEditStart: () => Promise<void>;
  onSave?: () => Promise<void>;
  onCancel?: () => Promise<void>;
  onCopyPreviousDay?: () => Promise<void>;
};

//=====================================
// 各ページの上に表示する項目
// 更新日、日付や公園名など
//=====================================
export const PageTop = memo((props: Props) => {
  const {
    title,
    subtitle,
    reportType,
    tabId,
    lastUpdated,
    showLastupdated = true,
    onEditStart,
    onSave,
    onCancel,
  } = props;
  const { selectedPark } = useContext(AuthContext);
  const { currentDate, selectDate } = useContext(StatusContext);
  const weatherRef = createRef<SaveDataHandler>();

  const [showCalendar, setShowCalendar] = useState(false);

  const toPrevDay = () => {
    const newDate = addDays(currentDate, -1);
    selectDate(newDate);
  };

  const toNextDay = () => {
    const newDate = addDays(currentDate, 1);
    selectDate(newDate);
  };

  const toToday = () => {
    selectDate(new Date());
  };

  const dateSelected = (date: Date) => {
    selectDate(date);
    setShowCalendar(false);
  };

  if (!selectedPark) return <div>公園が選択されていません</div>;

  return (
    <>
      <SForm className="sticky-top">
        <STopDiv className="ms-2 mt-0">
          <STopLeftDiv>
            <ReportDateDisplay
              date={currentDate}
              showDaySelectionRequested={() => setShowCalendar(true)}
              prevDayRequested={toPrevDay}
              nextDayRequested={toNextDay}
              todayRequested={toToday}
            />
            <WeatherCtrl ref={weatherRef} reportType={reportType} />
          </STopLeftDiv>
          {onSave && onCancel && (
            <EditControl
              parkId={selectedPark.parkId}
              editKey={genEditKey(currentDate)}
              reportType={reportType}
              tabId={tabId}
              onEditStart={onEditStart}
              onSave={async () => {
                // 先に日報を保存し、天気を作る時には必ずデータが存在するようにする
                // ※ onSaveの後はweatherRefがnullになるので先に取っておく
                const ref = weatherRef.current;
                await onSave();
                await ref?.save();
              }}
              onCancel={async () => {
                weatherRef.current?.canecl();
                await onCancel();
              }}
              onCopyPreviousDay={props.onCopyPreviousDay}
            />
          )}
        </STopDiv>
        <STitleDiv>
          {/* ページタイトル */}
          <div>
            <span className="h4">{title}</span>
            {subtitle && <span style={{ marginLeft: "0.5rem" }}> ({subtitle})</span>}
          </div>

          {showLastupdated ? (
            <LastUpdatedCtrl lastUpdated={lastUpdated} className="ms-auto mt-auto" />
          ) : (
            <div className="mt-3" />
          )}
        </STitleDiv>
      </SForm>
      {showCalendar && (
        <ILCalendarDlg
          show={showCalendar}
          initialValue={currentDate}
          onOK={dateSelected}
          onCancel={() => setShowCalendar(false)}
        />
      )}
    </>
  );
});

const SForm = styled(Form)`
  background-color: #ffffff;
  margin-top: 5px;
  padding: 0.5rem 0rem;
`;

const STopDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const STopLeftDiv = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
`;

const STitleDiv = styled.div`
  display: flex;
  margin: 1rem 0.5rem 0 0.5rem;
  justify-content: space-between;
  flex-wrap: wrap;
  @media screen and (max-width: 768px) {
    margin-right: 1.5rem;
  }
`;
